import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericMasterHeader from './GenericMasterHeader';
import DocumentActionIcons from "./DocumentActionIcons";
import DocumentsMaterialSearch from "./DocumentsMaterialSearch";
import { DocumentMaterial } from "../types/Types";

interface DocumentMasterHeaderProps {
    isCreating: boolean;
    isEditing?: boolean;
    isSaving: boolean;
    hasChanges?: boolean;
    onReset: () => void;
    onSave: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onCopy?: () => void;
    hasDocument?: boolean;
    search: string;
    setSearch: (val: string) => void;
    searchField?: "code" | "name";
    setSearchField?: (val: "code" | "name") => void;
    docs: DocumentMaterial[];
    loading: boolean;
    page: number;
    totalPages: number;
    total: number;
    absoluteTotal: number;
    setPage: (p: number) => void;
    documentCode?: string;
    documentName?: string;
    documentTypeId?: string;
    currentDocumentNumber?: string;
    onDocumentSelect?: (doc: DocumentMaterial) => void;
    document?: DocumentMaterial;
    category?: string;
    subCategory?: string;
    subType?: string;
    children?: React.ReactNode;
    showSearch?: boolean;
    forceListNavigation?: boolean;
    onExcelUpload?: () => void;
    renderExcelExport?: () => React.ReactNode;
    renderPrintDropdown?: () => React.ReactNode;
}

const DocumentMasterHeader: React.FC<DocumentMasterHeaderProps> = ({
    isCreating,
    isEditing = false,
    isSaving,
    hasChanges = true,
    onReset,
    onSave,
    onEdit,
    onDelete,
    onCopy,
    hasDocument,
    search,
    setSearch,
    searchField,
    setSearchField,
    docs,
    loading,
    page,
    totalPages,
    total,
    absoluteTotal,
    setPage,
    documentCode,
    documentName,
    documentTypeId,
    currentDocumentNumber,
    onDocumentSelect,
    document,
    category,
    subCategory,
    subType,
    children,
    showSearch = true,
    forceListNavigation,
    onExcelUpload,
    renderExcelExport,
    renderPrintDropdown,
}) => {
    const { t } = useTranslation();
    // For "Header" type documents (like Buyers tab), hide actions
    const shouldShowActions = documentCode !== "Header" || showSearch;

    // Transfer: show "Transfer" then code (e.g. "2026"); others: code then name
    const isTransfer = subCategory === 'transfer';
    const entityLabel = isTransfer ? (t('sidebar.transfer') || 'Transfer') : (documentCode || '');
    const entityCode = isTransfer ? documentCode : documentName;
    const entityName = isTransfer ? documentCode : documentName;

    return (
        <GenericMasterHeader
            entityLabel={entityLabel}
            entityCode={entityCode}
            entityName={entityName}
            entityId={document?._id}
            shortcutCategory={category || 'material'}
            shortcutSubCategory={subCategory}
            shortcutSubType={subType}
            documentTypeId={documentTypeId}
            isCreating={isCreating}
            isEditing={isEditing}
            isSaving={isSaving}
            hasChanges={hasChanges}
            hasEntity={hasDocument}
            onSave={shouldShowActions ? onSave : undefined}
            onNew={shouldShowActions ? onReset : undefined}
            onCopy={shouldShowActions ? onCopy : undefined}
            onExcelUpload={onExcelUpload}
            renderExcelExport={renderExcelExport}
            renderPrintDropdown={renderPrintDropdown}
            showActions={shouldShowActions}
            renderSearch={shouldShowActions ? () => (
                <div className="flex items-center gap-1 min-w-0">
                    <DocumentActionIcons
                        isCreating={isCreating}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        onReset={onReset}
                        onSave={onSave}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onCopy={onCopy}
                        hasDocument={hasDocument}
                    />
                    <DocumentsMaterialSearch
                        search={search}
                        setSearch={setSearch}
                        searchField={searchField}
                        setSearchField={setSearchField}
                        docs={docs}
                        loading={loading}
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        absoluteTotal={absoluteTotal}
                        setPage={setPage}
                        documentCode={documentCode}
                        documentTypeId={documentTypeId}
                        currentDocumentNumber={currentDocumentNumber}
                        onSelect={onDocumentSelect}
                        hidePartnerColumn={subCategory === "transfer"}
                        showItemColumn={category === "work-order"}
                        forceListNavigation={forceListNavigation ?? subCategory === "transfer"}
                        inputWidth="28ch"
                    />
                </div>
            ) : undefined}
        >
            {children}
        </GenericMasterHeader>
    );
};

export default DocumentMasterHeader;
